import { DEFAULT_CUSTOMER_THEME_ID, resolveCustomerThemeId, type CustomerThemeId } from './customerThemes'

/** Currency id stored in shared place settings. */
export type CurrencyCode = 'rupiah' | 'dollar' | 'sgd'

export type { CustomerThemeId }

export interface SharedPlaceSettings {
  /** IANA timezone, e.g. Asia/Jakarta */
  timezone: string
  currency: CurrencyCode
  /** When false, Close day UI and scheduled close are disabled (default) */
  closeDayEnabled: boolean
  /** Operational open time HH:mm (24h) */
  openTime: string
  /** Operational close time HH:mm (24h) */
  closeTime: string
  /** Minutes after closeTime before scheduled close sequence runs */
  closingSequenceOffsetMinutes: number
  /** Auto-hide finished+paid orders from kanban/TV/customer after N minutes (0 = off) */
  boardAutoHideMinutes: number
  /** When false, hide per-item todo checkboxes on kanban/detail cards (default on) */
  boardItemChecklistEnabled: boolean
  /** Guest QR app color theme (whitelist). */
  themeId: CustomerThemeId
}

const CURRENCY_IDS: CurrencyCode[] = ['rupiah', 'dollar', 'sgd']

function isCurrencyCode(value: string): value is CurrencyCode {
  return CURRENCY_IDS.includes(value as CurrencyCode)
}

const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/

function normalizeTime(value: unknown, fallback: string): string {
  if (typeof value === 'string' && HH_MM.test(value)) return value
  return fallback
}

function normalizeTimezone(value: unknown, fallback: string): string {
  if (typeof value !== 'string' || !value.trim()) return fallback
  try {
    Intl.DateTimeFormat(undefined, { timeZone: value.trim() })
    return value.trim()
  } catch {
    return fallback
  }
}

function defaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export const DEFAULT_SHARED_PLACE_SETTINGS: SharedPlaceSettings = {
  timezone: defaultTimezone(),
  currency: 'rupiah',
  closeDayEnabled: false,
  openTime: '09:00',
  closeTime: '22:00',
  closingSequenceOffsetMinutes: 60,
  boardAutoHideMinutes: 5,
  boardItemChecklistEnabled: true,
  themeId: DEFAULT_CUSTOMER_THEME_ID,
}

export function mergeSharedPlaceSettings(
  partial: Partial<SharedPlaceSettings> | null | undefined,
): SharedPlaceSettings {
  if (!partial) return { ...DEFAULT_SHARED_PLACE_SETTINGS }
  const currency =
    partial.currency && isCurrencyCode(partial.currency)
      ? partial.currency
      : DEFAULT_SHARED_PLACE_SETTINGS.currency
  const offset =
    typeof partial.closingSequenceOffsetMinutes === 'number' &&
    Number.isFinite(partial.closingSequenceOffsetMinutes)
      ? Math.max(0, Math.round(partial.closingSequenceOffsetMinutes))
      : DEFAULT_SHARED_PLACE_SETTINGS.closingSequenceOffsetMinutes
  const autoHide =
    typeof partial.boardAutoHideMinutes === 'number' &&
    Number.isFinite(partial.boardAutoHideMinutes)
      ? Math.max(0, Math.round(partial.boardAutoHideMinutes))
      : DEFAULT_SHARED_PLACE_SETTINGS.boardAutoHideMinutes
  return {
    timezone: normalizeTimezone(partial.timezone, DEFAULT_SHARED_PLACE_SETTINGS.timezone),
    currency,
    closeDayEnabled: partial.closeDayEnabled === true,
    openTime: normalizeTime(partial.openTime, DEFAULT_SHARED_PLACE_SETTINGS.openTime),
    closeTime: normalizeTime(partial.closeTime, DEFAULT_SHARED_PLACE_SETTINGS.closeTime),
    closingSequenceOffsetMinutes: offset,
    boardAutoHideMinutes: autoHide,
    boardItemChecklistEnabled: partial.boardItemChecklistEnabled !== false,
    themeId: resolveCustomerThemeId(partial.themeId),
  }
}

/** Parse closeTime + offset into minutes since local midnight for comparison. */
export function scheduledCloseMinutesSinceMidnight(settings: SharedPlaceSettings): number {
  const [h, m] = settings.closeTime.split(':').map(Number)
  return h * 60 + m + settings.closingSequenceOffsetMinutes
}

/** Current local minutes since midnight in the place timezone. */
export function localMinutesSinceMidnight(iso: string, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso))
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0')
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0')
  return hour * 60 + minute
}

/** True when local hour is 0 (00:00–00:59) in the place timezone. */
export function isLocalMidnightHour(iso: string, timezone: string): boolean {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      hour12: false,
    }).format(new Date(iso)),
  )
  return hour === 0
}

export function shouldRunScheduledClose(
  iso: string,
  settings: SharedPlaceSettings,
  hasCloseRecordForBusinessDate: boolean,
): boolean {
  return scheduledCloseSkipReason(iso, settings, hasCloseRecordForBusinessDate) == null
}

/** Why scheduled close will not run; null means it should run. */
export function scheduledCloseSkipReason(
  iso: string,
  settings: SharedPlaceSettings,
  hasCloseRecordForBusinessDate: boolean,
): 'close_day_disabled' | 'already_closed' | 'before_trigger' | null {
  if (!settings.closeDayEnabled) return 'close_day_disabled'
  if (hasCloseRecordForBusinessDate) return 'already_closed'
  const nowMinutes = localMinutesSinceMidnight(iso, settings.timezone)
  const triggerMinutes = scheduledCloseMinutesSinceMidnight(settings)
  if (nowMinutes < triggerMinutes) return 'before_trigger'
  return null
}
